<template>
  <NuxtLink
    :to="articleTo"
    class="article-card">
    <!-- ── IMAGE DE FOND ─────────────────────────────────────────────── -->
    <div class="article-card__image">
      <img
        v-if="props.article.Image"
        :src="props.article.Image"
        :alt="props.article.Title"
        class="article-card__img" />
    </div>

    <!-- ── PANNEAU CONTENU (s'agrandit au hover) ─────────────────────────── -->
    <div class="article-card__panel">
      <!-- Titre toujours visible -->
      <div class="article-card__header">
        <h3 class="article-card__title">{{ props.article.Title }}</h3>
      </div>

      <!-- Description - révélée au hover -->
      <div class="article-card__description">
        <p class="article-card__desc-text">{{ props.article.Description }}</p>
      </div>

      <!-- Footer avec infos et CTA -->
      <div class="article-card__footer">
        <!-- Catégorie (rubrique de l'article) -->
        <div class="article-card__meta">
          <svg
            class="article-card__icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true">
            <path
              d="M4 6.5C4 5.12 5.12 4 6.5 4H10L12 6H17.5C18.88 6 20 7.12 20 8.5V17.5C20 18.88 18.88 20 17.5 20H6.5C5.12 20 4 18.88 4 17.5V6.5Z"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round" />
            <path
              d="M8 13H16"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round" />
          </svg>
          <span class="article-card__category">{{ props.article.Category }}</span>
        </div>

        <!-- Temps de lecture -->
        <div class="article-card__meta">
          <svg
            class="article-card__icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true">
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-width="1.8" />
            <path
              d="M12 7V12L15.5 14"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
          <span class="article-card__reading-time"
            >{{ props.article.ReadingTime }} min de lecture</span
          >
        </div>

        <!-- CTA "Lire l'article" -->
        <span class="article-card__cta"
          >Lire l'article <span class="article-card__arrow">›</span></span
        >
      </div>
    </div>
  </NuxtLink>
</template>

<script lang="ts" setup>
import type { ComputedRef, PropType } from 'vue'
import type { BlogArticleObj } from '~/types/school'

interface ArticleCardProps {
  article: BlogArticleObj
}

/**
 * Définit les props du composant ArticleCard.
 */
const props: ArticleCardProps = defineProps({
  article: {
    type: Object as PropType<BlogArticleObj>,
    required: true,
  },
})

const localePath: ReturnType<typeof useLocalePath> = useLocalePath()

const articleTo: ComputedRef<string> = computed<string>(() => {
  const link: string = (props.article.Link ?? '').trim()
  return localePath(link.length > 0 ? link : '/nos-conseils')
})
</script>

<style scoped>
/* ════════════════════════════════════════════════════════════
   ARTICLE CARD COMPONENT
   ════════════════════════════════════════════════════════════ */

.article-card {
  --card-image-height: 220px;
  --card-panel-min-height: 220px;
  --card-desc-max-height: 9.375rem;
  --card-title-lines: 2.75rem;

  position: relative;
  display: block;
  height: 100%;
  min-height: calc(var(--card-image-height) + var(--card-panel-min-height));
  border-radius: var(--radius-lg);
  overflow: hidden;
  text-decoration: none;
  transition:
    border-color var(--duration-base) var(--ease-out),
    box-shadow var(--duration-base) var(--ease-out),
    transform var(--duration-base) var(--ease-out);
  border: 1px solid var(--border-subtle);
}

/* prettier-ignore */
.article-card:hover {
  border-color: var(--accent-orange);
  box-shadow: var(--shadow-lg), 0 0 20px rgba(249, 115, 22, 0.25);
  transform: translateY(-4px);
}

/* ── IMAGE (fixe en haut, recouverte par le panneau au hover) ────────── */
.article-card__image {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 0;
  height: var(--card-image-height);
  overflow: hidden;
  background: linear-gradient(135deg, rgba(3, 160, 241, 0.1) 0%, rgba(249, 115, 22, 0.05) 100%);
}

.article-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--duration-base) var(--ease-out);
}

/* ── PANNEAU (bas de carte, monte au hover sans agrandir la carte) ───── */
.article-card__panel {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: var(--card-panel-min-height);
  max-height: var(--card-panel-min-height);
  padding: var(--space-5) var(--space-6) var(--space-6);
  overflow: hidden;
  background: var(--bg-surface);
}

.article-card:hover .article-card__panel {
  height: auto;
  max-height: 100%;
}

/* ── HEADER (titre — hauteur réservée pour 2 lignes) ───────────────── */
.article-card__header {
  flex-shrink: 0;
  min-height: var(--card-title-lines);
}

.article-card__title {
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: var(--leading-snug);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  transition: color var(--duration-fast) var(--ease-out);
}

.article-card:hover .article-card__title {
  color: var(--accent-orange);
}

/* ── DESCRIPTION (max-height 0 → scrollable, anime la hauteur du panneau) ─── */
.article-card__description {
  box-sizing: border-box;
  max-height: 0;
  margin-top: var(--space-1);
  margin-bottom: var(--space-5);
  padding-top: 0;
  overflow: auto;
  transition: all 0.4s ease;
}

.article-card:hover .article-card__description {
  max-height: var(--card-desc-max-height);
  padding-top: var(--space-5);
  -webkit-overflow-scrolling: touch;
}

.article-card__desc-text {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin: 0;
  line-height: var(--leading-normal);
}

/* ── FOOTER (infos + CTA) ─────────────────────────────────────────────── */
.article-card__footer {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  flex-shrink: 0;
  margin-top: auto;
  padding-top: var(--space-4);
  border-top: 1px solid var(--border-subtle);
}

/* Metas (catégorie, temps de lecture) */
.article-card__meta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.article-card__icon {
  flex-shrink: 0;
  color: var(--accent-orange);
  opacity: 0.85;
}

.article-card__category,
.article-card__reading-time {
  opacity: 0.85;
}

/* CTA "Lire l'article" */
.article-card__cta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-secondary);
  transition: all var(--duration-fast) var(--ease-out);
}

.article-card:hover .article-card__cta {
  color: var(--accent-orange);
}

.article-card__arrow {
  display: inline-block;
  transition: transform var(--duration-fast) var(--ease-out);
}

.article-card:hover .article-card__arrow {
  transform: translateX(4px);
}

/* ── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 768px) {
  .article-card {
    --card-image-height: 180px;
    --card-panel-min-height: 200px;
    --card-title-lines: 2.5rem;
  }

  .article-card__title {
    font-size: var(--text-base);
  }

  .article-card__desc-text {
    font-size: var(--text-xs);
  }

  /* Mobile : panneau étendu + description ouverte (pas de hover fiable) */
  .article-card__panel {
    height: auto;
    max-height: 100%;
  }

  .article-card__description {
    max-height: var(--card-desc-max-height);
    padding-top: var(--space-5);
    -webkit-overflow-scrolling: touch;
  }

  .article-card__footer {
    padding-top: var(--space-3);
  }
}

@media (max-width: 640px) {
  .article-card {
    --card-image-height: 160px;
    --card-panel-min-height: 180px;
    --card-title-lines: 2.25rem;
  }

  .article-card__title {
    font-size: var(--text-sm);
  }

  .article-card__cta {
    font-size: var(--text-xs);
  }
}
</style>
